import { CheckIcon, PencilIcon, SplitIcon, XIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/core/button';

/**
 * The panel header: condition icon, rule label, and (when `editable`) an
 * inline rename. Buffers locally; flushes on tick/blur/Enter, Escape discards.
 */
export function ConditionHeader({
  name,
  editable,
  onChange,
  onClose,
}: {
  name: string;
  editable: boolean;
  onChange: (name: string) => void;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  const commit = () => {
    setEditing(false);
    if (draft !== name) {
      onChange(draft);
    }
  };

  return (
    <div className="flex items-center gap-2 border-b p-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <SplitIcon className="size-3.5" />
      </span>
      {editable && editing ? (
        <>
          <input
            autoFocus
            aria-label="Condition name"
            value={draft}
            className="min-w-0 flex-1 bg-transparent text-base font-medium outline-none"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commit();
              }
              if (event.key === 'Escape') {
                setEditing(false);
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Save name"
            // Keep the input focused through the press: its blur would commit and
            // swap this button back to Close before the click lands.
            onMouseDown={(event) => event.preventDefault()}
            onClick={commit}
          >
            <CheckIcon className="size-4" />
          </Button>
        </>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate text-base font-medium">{name || 'Untitled rule'}</span>
            {editable && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                aria-label="Edit name"
                onClick={() => {
                  setDraft(name);
                  setEditing(true);
                }}
              >
                <PencilIcon className="size-3.5" />
              </Button>
            )}
          </div>
          <Button type="button" variant="outline" size="icon" aria-label="Close" onClick={onClose}>
            <XIcon className="size-4" />
          </Button>
        </>
      )}
    </div>
  );
}
